import { ApiCommunications, FileResponse, NullPromise } from '../../interop/webmodule-interop.js';
import { ApiError, isProblemDetails, ProblemDetails, ProblemTypes } from '../../interop/problem-details.js';
import { currentUserClaims, IUserClaims } from '../currentuser-claims';
import { FetchError } from '../../../scriptsLicenseServer/api/licenseserver-api';
import {
  getApiToken,
  getCurrentUser,
  ResultTenantLogin,
  setApiToken,
  setCurrentUser,
  UserPublicInfo
} from './current-user';
import { strDuplicateLogin, strNetworkUnavailable } from './network-consts.js';
import pako from 'pako';
import type {
  ApiCommunicationsConfig,
  IApiErrorHandler,
  IAuthenticationHandler,
  IServiceRestartHandler
} from './api-communications-handlers.js';
import type { ValidationError } from './validation-error.js';

export const strInvalidBuildNumber = 'INVALID BUILD NUMBER';

/**
 * Configuration for service restart retry behavior
 */
export interface ServiceRestartRetryConfig {
  initialDelayMs: number;
  maxDelayMs: number;
  maxRetries: number;
}

const defaultServiceRestartConfig: ServiceRestartRetryConfig = {
  initialDelayMs: 2000,
  maxDelayMs: 30000,
  maxRetries: 5
};

// Header name for service restart detection
const headerServiceRestarting = 'X-Service-Restarting';

/**
 * Checks if a response indicates a planned service restart
 */
function isServiceRestarting(response: Response | null): boolean {
  if (!response) return false;
  return response.status === 503 && response.headers.get(headerServiceRestarting) === 'true';
}

/**
 * Calculates exponential backoff delay
 */
function calculateBackoffDelay(attemptNumber: number, config: ServiceRestartRetryConfig): number {
  const delay = config.initialDelayMs * Math.pow(2, attemptNumber);
  return Math.min(delay, config.maxDelayMs);
}

/**
 * Base class for API communications with injectable handlers.
 * Provides core HTTP communication logic with support for:
 * - Token-based authentication
 * - GZIP compression for large payloads
 * - Service restart detection and retry
 * - ProblemDetails error parsing
 * - Authentication retry workflow
 *
 * Subclasses or configurations can provide different handlers for:
 * - Authentication (UI modal vs event-based)
 * - Error display (toasts vs events)
 * - Service restart notifications
 */
export class ApiCommunicationsBase implements ApiCommunications {
  protected endpoint: string;
  protected authHandler: IAuthenticationHandler;
  protected errorHandler: IApiErrorHandler;
  protected serviceRestartHandler?: IServiceRestartHandler;
  protected clientTokenProvider?: () => string;

  // Flag to prevent multiple concurrent authentication attempts
  private _isAuthenticating = false;
  private _authPromise: Promise<boolean> | null = null;

  get activeUser(): UserPublicInfo | null {
    return getCurrentUser();
  }

  get activeUserClaims(): IUserClaims | null {
    return currentUserClaims();
  }

  constructor(config: ApiCommunicationsConfig) {
    this.endpoint = config.endpoint;
    this.authHandler = config.authHandler;
    this.errorHandler = config.errorHandler;
    this.serviceRestartHandler = config.serviceRestartHandler;
    this.clientTokenProvider = config.clientTokenProvider;
  }

  fullUrl(path: string): string {
    return `${this.endpoint}/${path}`;
  }

  virtualPathToUrl(virtualPath: string): string {
    return `${this.endpoint}/api/File/${virtualPath}`;
  }

  getToken(): string {
    return getApiToken();
  }

  /**
   * Get the version check header value.
   * Override in subclasses for custom behavior.
   */
  protected getVersionCheck(): string {
    return 'no-check';
  }

  /**
   * Called after successful authentication to apply additional state.
   * Override in subclasses to save user session, etc.
   */
  protected async onAuthenticationSuccess(result: ResultTenantLogin): Promise<void> {
    setApiToken(result.authenticationToken);
    await setCurrentUser(result.publicInfo);
  }

  protected buildSecureHeaders(token: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Version-Check': this.getVersionCheck(),
      'Content-Type': 'application/json',
      Authorization: `bearer ${token}`
    };

    if (this.clientTokenProvider) {
      headers['Authorization-Client'] = this.clientTokenProvider();
    }

    return headers;
  }

  /**
   * Check if GZIP compression should be used.
   * Override in subclasses for custom behavior.
   */
  protected supportsGzip(): boolean {
    return globalThis.dealerConfiguration?.compression?.gzip ?? false;
  }

  /**
   * Retry a fetch operation when the service is restarting, using exponential backoff.
   */
  protected async retryFetchOnServiceRestart(
    url: string,
    fetchOptions: RequestInit,
    config: ServiceRestartRetryConfig = defaultServiceRestartConfig
  ): Promise<Response | null> {
    for (let attempt = 0; attempt < config.maxRetries; attempt++) {
      // Notify on first attempt
      if (attempt === 0) {
        this.serviceRestartHandler?.onServiceRestarting();
      }

      const delay = calculateBackoffDelay(attempt, config);
      await new Promise(resolve => setTimeout(resolve, delay));

      try {
        const response = await fetch(url, fetchOptions);

        // If service is still restarting, continue retrying
        if (isServiceRestarting(response)) {
          console.log(`Service still restarting, retry ${attempt + 1}/${config.maxRetries}`);
          continue;
        }

        // Service is back, return the response
        return response;
      } catch (error) {
        console.error(`Retry attempt ${attempt + 1} failed:`, error);
        continue;
      }
    }

    // All retries exhausted
    console.error(`Service restart: all ${config.maxRetries} retries exhausted`);
    this.serviceRestartHandler?.onServiceRestartFailed();
    return null;
  }

  /**
   * Core fetch method with retry logic, gzip compression, and service restart handling.
   */
  public async tryFetch(
    url: string,
    token: string,
    data: unknown,
    retryCount = 3,
    method = 'POST'
  ): NullPromise<Response> {
    let retry = retryCount > 0 ? 0 : -1;
    const supportGzip = this.supportsGzip();

    while (retry++ <= retryCount) {
      try {
        let body: BodyInit | undefined = undefined;
        const headers = this.buildSecureHeaders(token);

        // Only gzip if data is present, method is not GET, and supportGzip is true
        if (data && method !== 'GET' && supportGzip) {
          const jsonString = JSON.stringify(data);
          const beforeSize = jsonString.length;
          const TCP_PACKET_SIZE = 1400;
          if (beforeSize < TCP_PACKET_SIZE) {
            body = jsonString;
          } else {
            const gzipped = pako.gzip(jsonString);
            body = new Blob([gzipped], { type: 'application/json' });
            headers['Content-Encoding'] = 'gzip';
          }
        } else if (data) {
          body = JSON.stringify(data);
        }

        const fetchOptions: RequestInit = {
          method: method,
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'same-origin',
          headers,
          redirect: 'follow',
          referrerPolicy: 'no-referrer',
          body
        };

        const httpresponse = await fetch(url, fetchOptions);

        // Handle planned service restart (503 with X-Service-Restarting header)
        if (isServiceRestarting(httpresponse)) {
          const retryResponse = await this.retryFetchOnServiceRestart(url, fetchOptions);
          return retryResponse;
        }

        return httpresponse;
      } catch (e) {
        console.error(e);
        await new Promise(resolve => setTimeout(resolve, retry * 1000));
      }
    }
    return null;
  }

  /**
   * Fetch a file with authentication and retry logic.
   */
  public async getFile(
    url: string,
    options?: {
      method?: 'GET' | 'POST';
      body?: BodyInit | null;
      retryCount?: number;
    }
  ): Promise<Response> {
    const token = this.getToken();
    if (!getCurrentUser() || token === '') {
      return new Response(null, { status: 401, statusText: 'Unauthorized' });
    }

    const retryCount = options?.retryCount ?? 3;
    const method = options?.method ?? 'GET';
    let retry = retryCount > 0 ? 0 : -1;

    while (retry++ <= retryCount) {
      try {
        const fetchOptions: RequestInit = {
          method,
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'same-origin',
          headers: this.buildSecureHeaders(token),
          redirect: 'follow',
          referrerPolicy: 'no-referrer',
          body: options?.body ?? undefined
        };

        const response = await fetch(url, fetchOptions);

        // Handle planned service restart
        if (isServiceRestarting(response)) {
          const retryResponse = await this.retryFetchOnServiceRestart(url, fetchOptions);
          return retryResponse ?? new Response(null, { status: 503, statusText: 'Service Unavailable' });
        }

        return response;
      } catch (error) {
        console.error(error);
        await new Promise(resolve => setTimeout(resolve, retry * 1000));
      }
    }

    return new Response(null, { status: 401, statusText: 'Unauthorized' });
  }

  /**
   * Download a file via POST request.
   */
  public async postFileDownload(path: string, data?: unknown): Promise<FileResponse | null> {
    const token = this.getToken();

    if (!getCurrentUser() || token === '') return null;

    try {
      const httpresponse = await this.tryFetch(`${this.endpoint}/${path}`, token, data);
      if (!httpresponse || httpresponse.status !== 200) {
        const str404 = `Api Mismatch: "${this.endpoint}/${path}" unavailable. Please upgrade the server`;
        let text = (await httpresponse?.text()) ?? strNetworkUnavailable;
        if (httpresponse?.status === 412) {
          text = strDuplicateLogin;
        }

        const msg =
          httpresponse?.status === 412
            ? text
            : httpresponse?.status === 404
              ? str404
              : `${httpresponse?.status ?? 1}: ${text}`;

        const apiError = new ApiError(
          {
            type: ProblemTypes.InternalError,
            title: 'File Download Error',
            status: httpresponse?.status ?? 500,
            detail: msg,
            instance: path
          },
          httpresponse ?? undefined
        );
        await this.handleApiError(apiError);
        return null;
      } else {
        const _headers: Record<string, unknown> = {};
        if (httpresponse.headers?.forEach) {
          httpresponse.headers.forEach((v, k) => (_headers[k] = v));
        }

        const contentDisposition = httpresponse.headers?.get('content-disposition');
        const fileNameMatch = contentDisposition ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition) : undefined;
        const fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;

        const blob = await httpresponse.blob();
        return {
          fileName: fileName,
          data: blob,
          status: httpresponse.status,
          headers: _headers
        };
      }
    } catch (e) {
      const apiError =
        e instanceof ApiError
          ? e
          : new ApiError({
              type: ProblemTypes.InternalError,
              title: 'File Download Error',
              status: 500,
              detail: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
              instance: path
            });
      await this.handleApiError(apiError);
      return null;
    }
  }

  /**
   * GET request with authentication workflow.
   */
  public async get<ResultType>(
    path: string,
    skipErrorDisplay?: boolean,
    convertRaw?: boolean
  ): Promise<ResultType | null> {
    const token = this.getToken();

    if (!getCurrentUser() || token === '') return null;

    const retVal = await this.authenticationWorkflow<ResultType>(() =>
      this.apiRequest<ResultType>(`${this.endpoint}/${path}`, undefined, skipErrorDisplay, convertRaw, 'GET')
    );

    if (retVal === null) {
      throw new FetchError(500, 'Failed to get data');
    }
    return retVal;
  }

  /**
   * POST request with authentication workflow.
   */
  public async post<T>(
    path: string,
    data?: unknown,
    skipShowResponseError?: boolean,
    convertRaw?: boolean
  ): Promise<T | null> {
    if (!data) data = {};
    const retVal = await this.authenticationWorkflow<T>(() =>
      this.apiRequest<T>(`${this.endpoint}/${path}`, data, skipShowResponseError, convertRaw)
    );

    return retVal;
  }

  /**
   * Strict variant of post(): enforces contract that a successful call always returns a non-null result.
   */
  public async postStrict<T>(
    path: string,
    data?: unknown,
    skipShowResponseError?: boolean,
    convertRaw?: boolean
  ): Promise<T> {
    const result = await this.post<T>(path, data, skipShowResponseError, convertRaw);
    if (result === null) {
      throw new FetchError(500, `POST ${path} returned null (treated as error)`);
    }
    return result;
  }

  /**
   * Core API request method with error handling and ProblemDetails parsing.
   */
  protected async apiRequest<T>(
    url: string,
    data: unknown,
    skipShowResponseError?: boolean,
    _convertRaw?: boolean,
    method: 'POST' | 'GET' = 'POST'
  ): Promise<T> {
    const token = this.getToken();
    if (token === '') {
      throw new ApiError(
        {
          type: 'https://api.dealerwebmodule.com/errors/unauthenticated',
          title: 'Unauthenticated',
          status: 401,
          detail: 'No authentication token available.'
        },
        new Response(null, { status: 401 })
      );
    }

    try {
      const httpresponse = await this.tryFetch(url, token, data, 3, method);

      if (!httpresponse) {
        throw new ApiError(
          {
            type: 'https://api.dealerwebmodule.com/errors/internal-error',
            title: 'Network Error',
            status: 0,
            detail: strNetworkUnavailable
          },
          null
        );
      }

      // Handle special HTTP status codes
      if (httpresponse.status === 412) {
        throw new ApiError(
          {
            type: 'https://api.dealerwebmodule.com/errors/already-connected',
            title: 'Already Connected',
            status: 412,
            detail: strDuplicateLogin
          },
          httpresponse
        );
      }

      if (httpresponse.status === 409) {
        throw new ApiError(
          {
            type: 'https://api.dealerwebmodule.com/errors/version-mismatch',
            title: 'Version Mismatch',
            status: 409,
            detail: strInvalidBuildNumber
          },
          httpresponse
        );
      }

      // 204 No Content: resource not found, return null silently
      if (httpresponse.status === 204) {
        return null as T;
      }

      // Success: 2xx status codes
      if (httpresponse.ok) {
        const responseData = await httpresponse.json();
        return responseData as T;
      }

      // Error: Try to parse as ProblemDetails
      const contentType = httpresponse.headers.get('content-type') ?? '';
      let problemDetails: ProblemDetails;

      if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
        try {
          const errorData = await httpresponse.json();
          if (isProblemDetails(errorData)) {
            problemDetails = errorData;
          } else {
            problemDetails = {
              type: 'https://api.dealerwebmodule.com/errors/internal-error',
              title: 'Error',
              status: httpresponse.status,
              detail: JSON.stringify(errorData)
            };
          }
        } catch {
          const text = await httpresponse.text();
          problemDetails = {
            type: 'https://api.dealerwebmodule.com/errors/internal-error',
            title: 'Error',
            status: httpresponse.status,
            detail: text || `HTTP ${httpresponse.status}`
          };
        }
      } else {
        const text = await httpresponse.text();
        problemDetails = {
          type: 'https://api.dealerwebmodule.com/errors/internal-error',
          title: 'Error',
          status: httpresponse.status,
          detail: text || `HTTP ${httpresponse.status}`
        };
      }

      const apiError = new ApiError(problemDetails, httpresponse);
      if (!skipShowResponseError) {
        await this.handleApiError(apiError);
      }
      throw apiError;
    } catch (e) {
      if (e instanceof ApiError) {
        throw e;
      }

      // Network or other errors
      const error = e instanceof Error ? e : new Error(String(e));
      const problemDetails: ProblemDetails = {
        type: 'https://api.dealerwebmodule.com/errors/internal-error',
        title: 'Network Error',
        status: 0,
        detail: `url:${url}\n${error.name}: ${error.message}`,
        stackTrace: error.stack
      };
      const apiError = new ApiError(problemDetails, null);
      if (!skipShowResponseError) {
        await this.handleApiError(apiError);
      }
      throw apiError;
    }
  }

  /**
   * Perform the authentication process using the injected handler.
   * Returns true if authentication succeeded, false otherwise.
   */
  public async performAuthenticationProcess(): Promise<boolean> {
    // Prevent multiple concurrent authentication attempts
    if (this._isAuthenticating && this._authPromise) {
      return this._authPromise;
    }

    this._isAuthenticating = true;
    this._authPromise = this._doAuthentication();

    try {
      return await this._authPromise;
    } finally {
      this._isAuthenticating = false;
      this._authPromise = null;
    }
  }

  private async _doAuthentication(): Promise<boolean> {
    try {
      setApiToken(null);
      const result = await this.authHandler.authenticate();

      if (getApiToken() === '') {
        if (result && result.authenticationToken !== '' && result?.authenticationToken) {
          await this.onAuthenticationSuccess(result);
          return true;
        }
      } else {
        return true;
      }
    } catch {
      setTimeout(() => {
        this.authHandler.onAuthenticationFailed();
      }, 5);
      return false;
    }
    return false;
  }

  /**
   * Authentication workflow: handles authentication retry on 401 errors.
   * Returns result on success, null on unrecoverable error.
   */
  protected async authenticationWorkflow<T>(callback: () => Promise<T>): NullPromise<T> {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        return await callback();
      } catch (e) {
        if (!(e instanceof ApiError)) {
          throw e;
        }

        if (e.isAuthentication) {
          if (await this.performAuthenticationProcess()) {
            continue; // Retry the request
          } else {
            await this.errorHandler.handleError(e);
            return null;
          }
        }

        if (e.isAuthorization) {
          await this.errorHandler.handleError(e);
          return null;
        }

        if (e.isValidation) {
          const validationErrors = e.validationErrors as ValidationError[];
          if (validationErrors) {
            await this.errorHandler.handleValidationErrors(validationErrors);
          }
          return null;
        }

        // Other errors (412 duplicate login, not-found, server error, etc.)
        // Delegate to error handler which will show appropriate UI (e.g., duplicate login dialog)
        await this.errorHandler.handleError(e);
        return null;
      }
    }
  }

  /**
   * Handle API error by delegating to the error handler.
   */
  protected async handleApiError(error: ApiError): Promise<void> {
    if (!error.isAuthentication && !error.isAuthorization && !error.isValidation) {
      await this.errorHandler.handleError(error);
    }
  }
}
